// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.TopMenu.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * TopMenu component
 */
  import React, { Component } from 'react';
  import config from 'config';

  // data imports
  import TopMenuStore from 'logic/Izenda.TopMenuStore';
  import uiState from 'logic/Izenda.UiState';

  // share imports
  import connect from 'share/enhancers/Izenda.Connect';
  import LayoutUtils from 'share/utils/Izenda.LayoutUtils';
  import routeUtils from 'share/utils/Izenda.RouteUtils';

  import IntegrationStyleAction from 'pages/Izenda.IntegrationStyleAction';
  const integrationStyleAction = IntegrationStyleAction.checkDisplayByKey('hideTopMenu');

  /**
   * @class TopMenu
   * @description render the Menu for the header of the platform
   */
  @connect(integrationStyleAction)
  class TopMenu extends Component {
    constructor(props) {
      super(props);
      this.topMenuStore = new TopMenuStore();
      this.logoElm = null;
      this.logoImage = new Image();
    }

    /**
     * @method componentDidMount
     * @description Set the logo div width once the component has mounted and the
     * background image has been set in css
     */
    componentDidMount() {
      this.intializeLogoImage();
    }

    /**
     * @method componentWillUnmount
     * @description remove events before removing component from dom
     */
    componentWillUnmount() {
      if (this.logoElm) {
        this.logoImage.removeEventListener('load', this.getLogoImageWidth);
      }
    }

    /**
     * @method intializeLogoImage
     * @description grab the logo image from css and load it through js to get the width
     */
    intializeLogoImage = () => {
      if (this.logoElm) {
        const backgroundImg = window.getComputedStyle(this.logoElm).getPropertyValue("background-image");
        const backgroundImgUrl = backgroundImg.replace(/url\(['"]*(.*?)['"]*\)/g, '$1');
        this.logoImage.addEventListener('load', this.getLogoImageWidth);
  
        this.logoImage.src = backgroundImgUrl;
      }
    }

    /**
     * @method getLogoImageWidth
     * @description callback for image load event to set the logo div width based on the image width
     */
    getLogoImageWidth = () => {
      this.topMenuStore.setImageLogoWidth(this.logoImage.width);
    }

    /**
     * @method showExpandedMenu
     * @description toggle mobile menu
     */
    showExpandedMenu = () => {
      LayoutUtils.toggleTopMenu();
    }

    /**
     * @method getRouteUrl
     * @description get full route based on environment
     * @param {string} routeUrl absolute path from menuItem
     */
    getRouteUrl(route) {
      const {
        configFn,
        param
      } = route;
      return config.Integration ?
        routeUtils.getPathForIntegration(routeUtils[configFn](param)) :
        routeUtils[configFn](param);
    }

    /**
     * @method isActive
     * @description check if the menuItem is currently active
     * @param {Object} item menu item
     */
    isActive(item) {
      const { router } = this.props;
      if (router.isActive(routeUtils[item.route.configFn](item.route.param))) {
        if (item.children.length > 0) {
          return item.children.filter(
            child => router.isActive(routeUtils[child.route.configFn](child.route.param))
          ).length ===0;
        }
        return true;
      }
      return false;
    }

    isActiveSubLink(link, index) {
      const { router } = this.props;
      if (router.isActive(routeUtils[link.route.configFn](link.route.param))) {
        return true;
      }
      return false;
    }
    /**
     * @method hideMenu
     * @description hide the menu when the user navigates and check if the user has unsaved changes
     */
    hideMenu = (e, route) => {
      const {
        props
      } = this;
      if (e.button === 0)
        e.preventDefault();
      LayoutUtils.toggleTopMenu(false);

      props.confirmDirtyForm(uiState.isDirty, { onConfirmDirtyForm: uiState.onOkDirtyForm }).then(function() {
        props.router.push(routeUtils[route.configFn](route.param));
      });
    }

    /**
     * @method renderMenuItemChildren
     * @description render sub menu items
     * @param {Object[]} links children of main menu item
     * @param {string} parentKey key of the item that the children belong to
     */
    renderMenuItemChildren = (links, parentKey) => {
      return (
        links.map((link, index) => (
          !(uiState.isMobile && link.isHiddenOnMobile) &&
            <a className={`TopMenu__subMenuLink ${this.isActiveSubLink(link, index) ? 'active' : ''}`}
               href={this.getRouteUrl(link.route)}
               onClick={(e) => this.hideMenu(e, link.route)}
               title={link.title}
               key={link.key}
               >
              <i className={`icomoon-plus`} />
            </a>
        ))
      );
    }

    /**
     * @method renderListMenu
     * @description render main menu items
     * @param {Object} item menu item being rendered
     */
    renderListMenu = (item) => {
      const { key, route, title, icon } = item;
      const isActive = this.isActive(item);
      return (
        !(uiState.isMobile && item.isHiddenOnMobile) &&
        <li key={item.key} 
            className={`izenda-TopMenu-nav__mainMenuItem  ${title ==='Explore' ? 'explore' : ''}`}
        >
          <a className={isActive ? 'izenda-TopMenu-nav__mainMenuItem--link active' : 'izenda-TopMenu-nav__mainMenuItem--link'}
             href={this.getRouteUrl(route)}
             key={route} 
             onClick={(e) => this.hideMenu(e, route)} 
          >
            <i className={`${icon} izenda-TopMenu-nav__mainMenuItem--icon`} />
            <span className='izenda-TopMenu-title'>{title}</span>
          </a>
          {
            (item.children.length > 0) &&
            this.renderMenuItemChildren(item.children, item.key)
          }
        </li>
      );
    }

    /**
     * @method render
     * @description render logo and main menu
     */
    render() {
      return (
        <div className="izenda-TopMenu">
            <div 
              className='izenda-TopMenu-logo'
              ref={(elm) => {this.logoElm = elm; }}
              style={{width: `${this.topMenuStore.logoImageWidth}px`}}
            />

          <nav className='izenda-TopMenu-nav' aria-expanded='false'>
            <ul className='izenda-TopMenu-nav__mainMenu'>
              {this.topMenuStore.topMenu.map(item => this.renderListMenu(item))}
            </ul>
          </nav>

          <div className='navbar-header'>
            <button type='button' className='navbar-toggle' onClick={this.showExpandedMenu}>
              <span className='icon-menu u-colorWhite' />
            </button>
          </div>
        </div>
      );
    }
  }

  export default TopMenu;



// WEBPACK FOOTER //
// ./ui/share/components/layout/Izenda.TopMenu.js